import type {
  CandleData,
  ComboStatus,
  ComboGenome,
  AlphaEvalReport,
  SignalData,
  OrderData,
  PositionData,
  RiskMetrics,
  HMMState,
  PortfolioData,
  RdLoopState,
  JobStatus,
} from '../types/gateway';

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function generateMockCandles(count = 200): CandleData[] {
  const candles: CandleData[] = [];
  let price = 67_500;
  const baseTime = Math.floor(Date.now() / 1000) - count * 14400;

  for (let i = 0; i < count; i++) {
    const change = randomBetween(-0.02, 0.02);
    const open = price;
    const close = price * (1 + change);
    const high = Math.max(open, close) * (1 + Math.random() * 0.005);
    const low = Math.min(open, close) * (1 - Math.random() * 0.005);
    const volume = randomBetween(100, 2000);

    candles.push({
      time: baseTime + i * 14400,
      open: Math.round(open * 100) / 100,
      high: Math.round(high * 100) / 100,
      low: Math.round(low * 100) / 100,
      close: Math.round(close * 100) / 100,
      volume: Math.round(volume * 10) / 10,
    });

    price = close;
  }

  return candles;
}

export function generateMockSignals(count = 15): SignalData[] {
  return Array.from({ length: count }, (_, i) => ({
    id: `sig-${Date.now() - i * 60000}`,
    time: Date.now() / 1000 - i * 3600,
    side: Math.random() > 0.5 ? 'BUY' as const : 'SELL' as const,
    qty: Math.round(randomBetween(0.01, 0.5) * 1000) / 1000,
    price: Math.round(randomBetween(66000, 69000) * 100) / 100,
    pnl: Math.round(randomBetween(-200, 500) * 100) / 100,
  }));
}

export function generateMockCombos(): ComboStatus[] {
  return [
    { combo: 'H007_momentum', win_rate: 0.517, pnl: 12340, weight: 0.33, cusum_score: 0.78, enabled: true },
    { combo: 'H008_mean_rev', win_rate: 0.514, pnl: 18920, weight: 0.40, cusum_score: 0.85, enabled: true },
    { combo: 'H010_vol_hyb', win_rate: 0.521, pnl: 15670, weight: 0.27, cusum_score: 0.82, enabled: true },
  ];
}

export function generateMockGenomes(): ComboGenome[] {
  const ts = Date.now() / 1000 - 86400;
  return [
    // ── Existing 3 ──────────────────────────────────────────────────────────
    {
      name: 'H007_momentum',
      bar_type: 'dollar_bar',
      instrument: 'BTCUSDT',
      model_path: 'models/onnx/BTCUSDT/H007/fold_27.onnx',
      strategy_class: 'accumulation',
      tactic: 'bracket',
      features: { extended: true, warmup_bars: 500, feature_count: 50, engine: 'tm-ta' },
      bracket: { direction_mode: 'long_only', tp_pct: 0.10, sl_pct: 0.20, snr_threshold: 0.67, k: 1.0 },
      scoring: { tier: 'A', score: 0.82, last_scored: ts },
      model_meta: { model_type: 'bayesian_ridge', trained_date: '2026-04-26', ic_at_training: 0.037, inference_ms: 0.80 },
    },
    {
      name: 'H008_mean_rev',
      bar_type: 'dollar_bar',
      instrument: 'ETHUSDT',
      model_path: 'models/onnx/ETHUSDT/H008/fold_27.onnx',
      strategy_class: 'reversion',
      tactic: 'bracket',
      features: { extended: true, warmup_bars: 500, feature_count: 50, engine: 'tm-ta' },
      bracket: { direction_mode: 'long_only', tp_pct: 0.10, sl_pct: 0.20, snr_threshold: 0.58, k: 1.0 },
      scoring: { tier: 'B', score: 0.47, last_scored: ts },
      model_meta: { model_type: 'bayesian_ridge', trained_date: '2026-04-26', ic_at_training: 0.071, inference_ms: 1.20 },
    },
    {
      name: 'H010_vol_hyb',
      bar_type: 'dollar_bar',
      instrument: 'BTCUSDT',
      model_path: 'models/onnx/BTCUSDT/H010/fold_27.onnx',
      strategy_class: 'accumulation',
      tactic: 'mean_reversion',
      features: { extended: true, warmup_bars: 500, feature_count: 50, engine: 'tm-ta' },
      bracket: { direction_mode: 'long_only', tp_pct: 0.10, sl_pct: 0.20, snr_threshold: 0.67, k: 1.0 },
      scoring: { tier: 'A', score: 0.63, last_scored: ts },
      model_meta: { model_type: 'bayesian_ridge', trained_date: '2026-04-26', ic_at_training: 0.095, inference_ms: 0.90 },
    },
    // ── New 9 ────────────────────────────────────────────────────────────────
    {
      name: 'btc-dollar-flip-v1',
      bar_type: 'dollar_bar',
      instrument: 'BTCUSDT',
      model_path: 'models/onnx/BTCUSDT/flip_v1/fold_31.onnx',
      strategy_class: 'accumulation',
      tactic: 'mean_reversion',
      features: { extended: true, warmup_bars: 400, feature_count: 45, engine: 'tm-ta' },
      bracket: { direction_mode: 'both', tp_pct: 0.12, sl_pct: 0.18, snr_threshold: 0.62, k: 1.1 },
      scoring: { tier: 'A', score: 0.74, last_scored: ts },
      model_meta: { model_type: 'lasso', trained_date: '2026-04-27', ic_at_training: 0.058, inference_ms: 0.65 },
    },
    {
      name: 'eth-dollar-follow-v2',
      bar_type: 'dollar_bar',
      instrument: 'ETHUSDT',
      model_path: 'models/onnx/ETHUSDT/follow_v2/fold_29.onnx',
      strategy_class: 'distribution',
      tactic: 'bracket',
      features: { extended: false, warmup_bars: 350, feature_count: 40, engine: 'tm-ta' },
      bracket: { direction_mode: 'long_only', tp_pct: 0.08, sl_pct: 0.16, snr_threshold: 0.55, k: 0.9 },
      scoring: { tier: 'B', score: 0.49, last_scored: ts },
      model_meta: { model_type: 'random_forest', trained_date: '2026-04-25', ic_at_training: 0.041, inference_ms: 1.45 },
    },
    {
      name: 'btc-volume-mean-rev-v1',
      bar_type: 'volume_bar',
      instrument: 'BTCUSDT',
      model_path: 'models/onnx/BTCUSDT/mean_rev_v1/fold_22.onnx',
      strategy_class: 'reversion',
      tactic: 'tb_signal',
      features: { extended: true, warmup_bars: 600, feature_count: 55, engine: 'tm-ta' },
      bracket: { direction_mode: 'both', tp_pct: 0.06, sl_pct: 0.12, snr_threshold: 0.48, k: 0.8 },
      scoring: { tier: 'C', score: 0.18, last_scored: ts },
      model_meta: { model_type: 'elastic_net', trained_date: '2026-04-20', ic_at_training: 0.022, inference_ms: 1.80 },
    },
    {
      name: 'sol-dollar-follow-v1',
      bar_type: 'dollar_bar',
      instrument: 'SOLUSDT',
      model_path: 'models/onnx/SOLUSDT/follow_v1/fold_30.onnx',
      strategy_class: 'accumulation',
      tactic: 'bracket',
      features: { extended: true, warmup_bars: 450, feature_count: 48, engine: 'tm-ta' },
      bracket: { direction_mode: 'long_only', tp_pct: 0.11, sl_pct: 0.19, snr_threshold: 0.72, k: 1.2 },
      scoring: { tier: 'S', score: 0.84, last_scored: ts },
      model_meta: { model_type: 'bayesian_ridge', trained_date: '2026-04-28', ic_at_training: 0.088, inference_ms: 0.55 },
    },
    {
      name: 'btc-tick-breakout-v2',
      bar_type: 'tick_bar',
      instrument: 'BTCUSDT',
      model_path: 'models/onnx/BTCUSDT/breakout_v2/fold_33.onnx',
      strategy_class: 'breakout',
      tactic: 'mean_reversion',
      features: { extended: false, warmup_bars: 300, feature_count: 38, engine: 'tm-ta' },
      bracket: { direction_mode: 'both', tp_pct: 0.09, sl_pct: 0.15, snr_threshold: 0.60, k: 1.0 },
      scoring: { tier: 'B', score: 0.62, last_scored: ts },
      model_meta: { model_type: 'gradient_boost', trained_date: '2026-04-26', ic_at_training: 0.052, inference_ms: 0.95 },
    },
    {
      name: 'eth-volume-vol-scaled-v1',
      bar_type: 'volume_bar',
      instrument: 'ETHUSDT',
      model_path: 'models/onnx/ETHUSDT/vol_scaled_v1/fold_28.onnx',
      strategy_class: 'reversion',
      tactic: 'tb_signal',
      features: { extended: true, warmup_bars: 500, feature_count: 52, engine: 'tm-ta' },
      bracket: { direction_mode: 'long_only', tp_pct: 0.10, sl_pct: 0.20, snr_threshold: 0.76, k: 1.3 },
      scoring: { tier: 'A', score: 0.76, last_scored: ts },
      model_meta: { model_type: 'lasso', trained_date: '2026-04-27', ic_at_training: 0.079, inference_ms: 0.70 },
    },
    {
      name: 'btc-dollar-wfo-v3',
      bar_type: 'dollar_bar',
      instrument: 'BTCUSDT',
      model_path: 'models/onnx/BTCUSDT/wfo_v3/fold_35.onnx',
      strategy_class: 'distribution',
      tactic: 'bracket',
      features: { extended: true, warmup_bars: 550, feature_count: 50, engine: 'tm-ta' },
      bracket: { direction_mode: 'long_only', tp_pct: 0.08, sl_pct: 0.14, snr_threshold: 0.53, k: 0.9 },
      scoring: { tier: 'B', score: 0.35, last_scored: ts },
      model_meta: { model_type: 'bayesian_ridge', trained_date: '2026-04-24', ic_at_training: 0.033, inference_ms: 0.85 },
    },
    {
      name: 'sol-volume-flip-v1',
      bar_type: 'volume_bar',
      instrument: 'SOLUSDT',
      model_path: 'models/onnx/SOLUSDT/flip_v1/fold_21.onnx',
      strategy_class: 'reversion',
      tactic: 'mean_reversion',
      features: { extended: false, warmup_bars: 250, feature_count: 35, engine: 'tm-ta' },
      bracket: { direction_mode: 'both', tp_pct: 0.05, sl_pct: 0.10, snr_threshold: 0.44, k: 0.7 },
      scoring: { tier: 'C', score: 0.13, last_scored: ts },
      model_meta: { model_type: 'elastic_net', trained_date: '2026-04-18', ic_at_training: 0.018, inference_ms: 2.00 },
    },
    {
      name: 'eth-dollar-breakout-v1',
      bar_type: 'dollar_bar',
      instrument: 'ETHUSDT',
      model_path: 'models/onnx/ETHUSDT/breakout_v1/fold_34.onnx',
      strategy_class: 'breakout',
      tactic: 'bracket',
      features: { extended: true, warmup_bars: 520, feature_count: 53, engine: 'tm-ta' },
      bracket: { direction_mode: 'both', tp_pct: 0.13, sl_pct: 0.22, snr_threshold: 0.78, k: 1.4 },
      scoring: { tier: 'S', score: 0.91, last_scored: ts },
      model_meta: { model_type: 'gradient_boost', trained_date: '2026-04-29', ic_at_training: 0.106, inference_ms: 0.50 },
    },
  ];
}

export function generateMockAlphaEval(): AlphaEvalReport {
  return {
    combo: 'H010_vol_hyb',
    ts: Date.now() / 1000,
    floor_gates: {
      ic_mean: { value: 0.095, pass: true },
      pbo: { value: 0.10, pass: true },
      oos_sharpe: { value: 0.67, pass: true },
      dsr: { value: 6.54, pass: true },
    },
    core_metrics: {
      sharpe: 8.39,
      win_rate: 0.86,
      profit_factor: 3.2,
      max_drawdown: -0.045,
      calmar: 12.1,
    },
    advanced_metrics: {
      sortino: 14.2,
      var_95: -0.012,
      cvar_95: -0.018,
      lo_sharpe: 7.8,
    },
    per_regime: {
      bull: { sharpe: 10.2, wr: 0.89, ic: 0.12, trades: 45 },
      bear: { sharpe: 5.1, wr: 0.78, ic: 0.07, trades: 22 },
      sideways: { sharpe: 7.8, wr: 0.85, ic: 0.09, trades: 33 },
    },
  };
}

export function generateMockRisk(): RiskMetrics {
  return {
    evt_es_99: -0.042,
    var_95: -0.018,
    liquidation_cascade_prob: 0.003,
    garch_vol: 0.0156,
    implied_skew: '-0.12',
  };
}

export function generateMockHMM(): HMMState[] {
  return [
    { name: 'Bull', label: 'BULL', probability: 0.65, color: '#01E599' },
    { name: 'Bear', label: 'BEAR', probability: 0.15, color: '#FF3366' },
    { name: 'Sideways', label: 'SIDE', probability: 0.20, color: '#FFD700' },
  ];
}

export function generateMockPortfolio(): PortfolioData {
  return {
    target_sharpe: 8.39,
    assets: [
      { name: 'BTCUSDT', weight: '0.70', weight_label: '70%', z_score: 1.2, factor: 'momentum', action: 'HOLD', action_color: '#01E599', border_hover_color: '#01E599' },
      { name: 'ETHUSDT', weight: '0.20', weight_label: '20%', z_score: -0.3, factor: 'mean_rev', action: 'REDUCE', action_color: '#FFD700', border_hover_color: '#FFD700' },
      { name: 'SOLUSDT', weight: '0.10', weight_label: '10%', z_score: 0.8, factor: 'vol_hyb', action: 'HOLD', action_color: '#01E599', border_hover_color: '#01E599' },
    ],
  };
}

export function generateMockOrders(): OrderData[] {
  return [
    { id: 'ord-001', symbol: 'BTCUSDT', side: 'BUY', type: 'MARKET', qty: 0.1, price: 67500, status: 'FILLED' },
    { id: 'ord-002', symbol: 'BTCUSDT', side: 'SELL', type: 'LIMIT', qty: 0.05, price: 68200, status: 'OPEN' },
    { id: 'ord-003', symbol: 'BTCUSDT', side: 'BUY', type: 'STOP_MARKET', qty: 0.08, price: 66800, status: 'OPEN' },
  ];
}

export function generateMockPositions(): PositionData[] {
  return [
    { symbol: 'BTCUSDT', qty: 0.15, entry_price: 67200, current_price: 70200, unrealized_pnl: 450, view: 'HEDGING', strategy: 'H010_vol_hyb' },
    { symbol: 'ETHUSDT', qty: 2.5, entry_price: 3200, current_price: 3152, unrealized_pnl: -120, view: 'HEDGING', strategy: 'H008_mean_rev' },
  ];
}

export function generateMockRdLoop(): RdLoopState {
  return {
    active_hypothesis: 'H012_microstructure_alpha',
    current_step: 'S5_EVALUATE',
    last_result: 'IC=0.087, Sharpe=1.45, DSR=+3.2 — PASS all floor gates',
    updated_at: new Date().toISOString(),
  };
}

export function generateMockJobs(): JobStatus[] {
  return [
    { name: 'cpcv_training_H012', status: 'Completed', created: '2026-05-05T10:00:00Z', duration: '45m' },
    { name: 'precompute_signals_BTCUSDT', status: 'Running', created: '2026-05-05T12:30:00Z', duration: '12m' },
    { name: 'combo_eval_3arm', status: 'Queued', created: '2026-05-05T13:00:00Z', duration: null },
  ];
}

export interface VarConeData {
  dates: string[];
  p05: number[];
  p25: number[];
  p50: number[];
  p75: number[];
  p95: number[];
}

export function generateMockVarCone(days = 30): VarConeData {
  const dates: string[] = [];
  const p05: number[] = [];
  const p25: number[] = [];
  const p50: number[] = [];
  const p75: number[] = [];
  const p95: number[] = [];

  const baseEquity = 369_393;
  const dailyVol = 0.012;

  for (let d = 0; d <= days; d++) {
    const date = new Date();
    date.setDate(date.getDate() + d);
    dates.push(date.toISOString().split('T')[0]);

    const sqrtT = Math.sqrt(d || 0.01);
    const drift = 0.0003 * d;

    p05.push((baseEquity * (1 + drift - 1.645 * dailyVol * sqrtT) - baseEquity) / baseEquity);
    p25.push((baseEquity * (1 + drift - 0.675 * dailyVol * sqrtT) - baseEquity) / baseEquity);
    p50.push((baseEquity * (1 + drift) - baseEquity) / baseEquity);
    p75.push((baseEquity * (1 + drift + 0.675 * dailyVol * sqrtT) - baseEquity) / baseEquity);
    p95.push((baseEquity * (1 + drift + 1.645 * dailyVol * sqrtT) - baseEquity) / baseEquity);
  }

  return { dates, p05, p25, p50, p75, p95 };
}
